import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/contexts/ToastContext";
import { CheckCircle2, XCircle, Loader2, Check, X, Info } from "lucide-react";
import AuthLayout from "@/components/layout/AuthLayout";
import { staffApi } from "@/lib/api/staff";
import { formatLibraryName } from "@/lib/utils";

function getPasswordStrength(pw: string): number {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score;
}

const strengthLabels = ["Weak", "Fair", "Good", "Strong"];
const strengthColors = ["bg-red-500", "bg-amber-500", "bg-blue-500", "bg-green-500"];

export default function StaffAcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<
    "idle" | "validating" | "ready" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirmPassword?: string }>(
    {}
  );
  const [staffInfo, setStaffInfo] = useState<{
    firstName: string;
    lastName: string;
    libraryName: string;
    tenantSlug: string;
    tenantDomains: string[];
  } | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid invite link. Please check your email for the correct invitation link.");
      return;
    }

    setStatus("validating");
    staffApi
      .validateInviteToken(token)
      .then((data) => {
        setStaffInfo(data);
        setStatus("ready");
      })
      .catch((err: any) => {
        setStatus("error");
        const errorMsg = err.response?.data?.message || err.message;
        setMessage(
          errorMsg ||
            "This invitation link is invalid or has expired. Please contact your administrator for a new invitation."
        );
      });
  }, [token]);

  const getTenantLoginUrl = (): string => {
    if (!staffInfo) return "/login";

    const tenantDomains = staffInfo.tenantDomains || [];
    const tenantSlug = staffInfo.tenantSlug;
    const suffix = import.meta.env.VITE_TENANT_DOMAIN_SUFFIX || "philolms.com";

    if (tenantDomains.length > 0) {
      let domain = tenantDomains[0];
      if (!domain.startsWith("http")) {
        domain = `https://${domain}`;
      }
      const normalized = domain.replace(/^https?:\/\//, "").split("/")[0];
      return `https://${normalized}/login`;
    }

    if (tenantSlug) {
      const isLocalhost = window.location.hostname.includes("localhost");
      if (isLocalhost) {
        return `/login?tenantSlug=${tenantSlug}`;
      }
      return `https://${tenantSlug}.${suffix}/login`;
    }

    return "/login";
  };

  const validateFields = () => {
    const errors: { password?: string; confirmPassword?: string } = {};
    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateFields()) return;

    if (!token) {
      toast.error("Invalid invite token.");
      setStatus("error");
      return;
    }

    setStatus("loading");

    try {
      await staffApi.acceptInvite({ token, password });
      setStatus("success");
      setMessage("Your account has been activated! Redirecting you to log in...");
      toast.success("Your account has been activated successfully!");

      const loginUrl = getTenantLoginUrl();
      setTimeout(() => {
        if (loginUrl.startsWith("http")) {
          window.location.href = loginUrl;
        } else {
          navigate(loginUrl);
        }
      }, 3000);
    } catch (err: any) {
      setStatus("ready");
      const errorMsg = err.response?.data?.message || err.message;
      toast.error(
        errorMsg ||
          "Failed to set password. The link may have expired. Please contact your administrator."
      );
    }
  };

  const headlineMap: Record<string, string> = {
    idle: "You've been invited.",
    validating: "You've been invited.",
    ready: "You've been invited.",
    loading: "You've been invited.",
    success: "Welcome aboard!",
    error: "You've been invited.",
  };

  const subtextMap: Record<string, string> = {
    idle: "Set up your staff account to start managing your library.",
    validating: "Verifying your invitation link...",
    ready: "Set up your staff account to start managing your library.",
    loading: "Activating your account...",
    success: "Your staff account is ready. Redirecting you to sign in.",
    error: "Something went wrong with your invitation link.",
  };

  const strength = password ? getPasswordStrength(password) : 0;

  return (
    <AuthLayout headline={headlineMap[status]} subtext={subtextMap[status]}>
      <div className="space-y-6 animate-fade-in-up">
        {/* --- Validating state --- */}
        {(status === "validating" || status === "idle") && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-sm text-slate-500">Verifying your invitation...</p>
          </div>
        )}

        {/* --- Success state --- */}
        {status === "success" && (
          <div className="text-center space-y-5">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 ring-8 ring-green-50/50">
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Account activated</h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{message}</p>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <p className="text-sm text-green-800 text-left">
                  Staff must log in from your library's portal. Redirecting you now...
                </p>
              </div>
            </div>

            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                const url = getTenantLoginUrl();
                if (url.startsWith("http")) window.location.href = url;
                else navigate(url);
              }}
            >
              Go to sign in
            </Button>
          </div>
        )}

        {/* --- Error state --- */}
        {status === "error" && (
          <div className="text-center space-y-5">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 ring-8 ring-red-50/50">
              <XCircle className="h-9 w-9 text-red-600" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Invitation error</h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{message}</p>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-800 text-left">
                  Please contact your administrator for a new invitation link.
                </p>
              </div>
            </div>

            <Button onClick={() => navigate("/")} className="w-full" size="lg">
              Go to home
            </Button>
          </div>
        )}

        {/* --- Form state (ready / loading) --- */}
        {(status === "ready" || status === "loading") && (
          <>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Set your password</h2>
              {staffInfo && (
                <p className="mt-1 text-sm text-slate-500">
                  Welcome,{" "}
                  <span className="font-medium text-slate-700">
                    {staffInfo.firstName} {staffInfo.lastName}
                  </span>
                  ! You've been invited to join{" "}
                  <span className="font-medium text-slate-700">
                    {formatLibraryName(staffInfo.libraryName)}
                  </span>
                  . Create a password to activate your account.
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password)
                      setFieldErrors((p) => ({ ...p, password: undefined }));
                  }}
                  placeholder="Enter your password"
                  disabled={status === "loading"}
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.password}
                  className={
                    fieldErrors.password
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                />
                {fieldErrors.password && (
                  <p className="text-sm text-red-600" role="alert">
                    {fieldErrors.password}
                  </p>
                )}

                {password && (
                  <div className="space-y-2 pt-1">
                    <div className="flex gap-1.5">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-colors ${
                            i < strength ? strengthColors[strength - 1] : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">
                      Strength: {strengthLabels[strength - 1] || "Too short"}
                    </p>

                    <div className="space-y-1">
                      {[
                        { met: password.length >= 8, label: "At least 8 characters" },
                        {
                          met: /[a-z]/.test(password) && /[A-Z]/.test(password),
                          label: "Upper & lowercase letters",
                        },
                        { met: /\d/.test(password), label: "At least one number" },
                        {
                          met: /[^a-zA-Z0-9]/.test(password),
                          label: "At least one special character",
                        },
                      ].map((req) => (
                        <div key={req.label} className="flex items-center gap-2 text-xs">
                          {req.met ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-slate-400" />
                          )}
                          <span className={req.met ? "text-green-700" : "text-slate-500"}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword)
                      setFieldErrors((p) => ({ ...p, confirmPassword: undefined }));
                  }}
                  placeholder="Confirm your password"
                  disabled={status === "loading"}
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.confirmPassword}
                  className={
                    fieldErrors.confirmPassword
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                />
                {confirmPassword && password === confirmPassword && (
                  <p className="flex items-center gap-1.5 text-xs text-green-600">
                    <Check className="h-3.5 w-3.5" /> Passwords match
                  </p>
                )}
                {fieldErrors.confirmPassword && (
                  <p className="text-sm text-red-600" role="alert">
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>

              <Button type="submit" disabled={status === "loading"} className="w-full" size="lg">
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  "Accept invitation & set password"
                )}
              </Button>
            </form>

            {/* Tenant login info */}
            {staffInfo && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm text-primary text-left">
                    After activating, log in at{" "}
                    <strong>{formatLibraryName(staffInfo.libraryName)}</strong>'s portal:{" "}
                    <strong>
                      {staffInfo.tenantDomains?.[0] || `${staffInfo.tenantSlug}.philolms.com`}
                    </strong>
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AuthLayout>
  );
}
